import React from 'react';
import {
  Box,
  HStack,
  Icon,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { Lightbulb } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { StudentEvaluationFeedback } from '@/common/api/models';
import { useTaskSubmissionState } from '../../stores/TaskSubmissionStore';
import AiTutorFormativeResponse from './AiTutorFormativeResponse';

type Props = {
  courseId: string;
  moduleId: string;
  taskId: string;
  initialFeedback?: StudentEvaluationFeedback;
  readonly?: boolean;
  hideStudentFeedbackControls?: boolean;
  emptyMessage?: string;
};

const FormativeFeedbackCard: React.FC<Props> = ({
  courseId,
  moduleId,
  taskId,
  initialFeedback,
  readonly = false,
  hideStudentFeedbackControls = false,
  emptyMessage,
}) => {
  const { t } = useTranslation();
  const storeState = useTaskSubmissionState();
  const {
    formativeFeedback, isEvaluating, formativeFeedbackVersion, formativeFeedbackRunId,
  } = storeState;

  if (isEvaluating) {
    return null;
  }

  if (!formativeFeedback) {
    return (
      <VStack space="md" flex={1}>
        <HStack alignItems="center" space="sm">
          <Icon as={Lightbulb} size="sm" color="$primary600" />
          <Text size="xs" fontWeight="bold" color="$textHint" textTransform="uppercase" letterSpacing="$md">
            {t('student.task.aiTutor.feedback.title')}
          </Text>
        </HStack>
        <Box bg="$bgCard" borderRadius="$xl" p="$4" borderWidth={1} borderColor="$borderLight">
          <Text size="xs" color="$textSubtle">
            {emptyMessage || t('student.task.aiTutor.feedback.empty')}
          </Text>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack space="md" flex={1}>
      <HStack alignItems="center" space="sm">
        <Icon as={Lightbulb} size="sm" color="$primary600" />
        <Text size="xs" fontWeight="bold" color="$textHint" textTransform="uppercase" letterSpacing="$md">
          {t('student.task.aiTutor.feedback.title')}
        </Text>
      </HStack>

      <VStack space="md" bg="$bgCard" borderRadius="$xl" p="$4" borderWidth={1} borderColor="$borderLight">
        <AiTutorFormativeResponse
          variant="card"
          courseId={courseId}
          moduleId={moduleId}
          taskId={taskId}
          observation={formativeFeedback.observation}
          nextStep={formativeFeedback.nextStep}
          isReadyToProgress={formativeFeedback.isReadyToProgress}
          formativeFeedbackVersion={formativeFeedbackVersion}
          runId={formativeFeedbackRunId}
          initialFeedback={initialFeedback}
          readonly={readonly}
          hideStudentFeedbackControls={hideStudentFeedbackControls}
        />
      </VStack>
    </VStack>
  );
};

export default FormativeFeedbackCard;
